import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@contexts/useAuth';
import { useCanAccess } from '@permissions/useFeatures';
import { MessageSquareIcon } from '@utils/icons';
import { Toasts } from '@components/ui/Toasts/Toasts';
import { useToasts } from '@components/ui/Toasts/useToasts';
import { ChatList } from './Sidebar/ChatList/ChatList';
import { CreateConversationModal, type ConversationSubmit } from './CreateConversationModal/CreateConversationModal';
import { ChatThread } from './Thread/ChatThread/ChatThread';
import { useChannels } from './hooks/useChannels';
import { useOpenChannel } from './hooks/useOpenChannel';
import { useCreateChannel } from './hooks/useCreateChannel';
import { useEditChannel } from './hooks/useEditChannel';
import { useProjectMembers } from './hooks/useProjectMembers';
import { OWNS_ERROR_FEEDBACK, UnreadProvider, useChannelReadState, useUnread } from '@d-orca/shared/client/hooks';
import { unreadStorage } from './live/storage';
import { ChatSocketProvider } from './live/ChatSocketProvider';
import { dmChannelId, otherDmMemberId } from './utils/dmChannelId';
import { makeVirtualDmChannel } from './utils/virtualDmChannel';
import type { CommsMember } from '@apis/comms';
import type { ChannelSummary } from '@interfaces/ICommsApi';
import styles from './CommsPage.module.css';

// Stable while members load, so memoised name lookups don't rerun every render.
const NO_MEMBERS: CommsMember[] = [];

// Single discriminated state drives one modal instance for the whole page.
type ConversationModalState =
  | null
  | { mode: 'create' }
  | { mode: 'edit'; channel: ChannelSummary };

export function CommsPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const { user } = useAuth();
  const channelsQuery = useChannels(siteId ?? null);
  const channels = channelsQuery.data?.channels ?? [];

  if (!siteId) return null;
  return (
    <UnreadProvider key={`${user?.id ?? 'anon'}-${siteId}`} userId={user?.id ?? null} siteId={siteId} storage={unreadStorage}>
      <ChatSocketProvider siteId={siteId}>
        <CommsPanels siteId={siteId} channels={channels} channelsFetching={channelsQuery.isFetching} />
      </ChatSocketProvider>
    </UnreadProvider>
  );
}

interface IPanelsProps {
  siteId: string;
  channels: ChannelSummary[];
  channelsFetching: boolean;
}

function CommsPanels({ siteId, channels, channelsFetching }: IPanelsProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const currentUserId = user?.id ?? 'me';
  const { channel: realChannel, requestedId, open } = useOpenChannel(channels);

  // A requested DM that hasn't been persisted yet has no
  // entry in `channels` — synthesize a placeholder so the thread still opens.
  // Becomes moot the instant the real channel appears in `channels` (same
  // deterministic id), since `realChannel` then wins over this branch.
  const channel = useMemo(() => {
    if (realChannel) return realChannel;
    if (!requestedId) return null;
    const targetUserId = otherDmMemberId(requestedId, siteId, currentUserId);
    return targetUserId ? makeVirtualDmChannel(siteId, currentUserId, targetUserId) : null;
  }, [realChannel, requestedId, siteId, currentUserId]);
  const isVirtual = channel !== null && !realChannel;

  // Creating a channel is `comms.channels`; DMs need only `comms.post`.
  const canManageChannels = useCanAccess('comms.channels');

  const [conversationModal, setConversationModal] = useState<ConversationModalState>(null);
  // Both failures toast here (the page's own stack), so the app-wide backstop stays quiet.
  const createChannel = useCreateChannel(siteId, OWNS_ERROR_FEEDBACK);
  const editChannel = useEditChannel(siteId, OWNS_ERROR_FEEDBACK);
  const membersQuery = useProjectMembers(siteId);
  const members = membersQuery.data?.members ?? NO_MEMBERS;
  const { toasts, push: pushToast, dismiss: dismissToast } = useToasts();

  function openDmWith(targetUserId: string) {
    if (targetUserId === currentUserId) return;
    open(dmChannelId(siteId, currentUserId, targetUserId));
  }

  // Read state (unread counts, server sync, mark-read, "New messages" divider).
  const { unreadSinceAt, unreadAtOpenCount, lastReadMap, countMap } = useChannelReadState({
    siteId,
    channels,
    channelsFetching,
    activeChannelId: channel?.id ?? null,
    activeLastMessageAt: channel?.lastMessageAt,
    store: useUnread(),
  });

  function handleCreateConversationSubmit(payload: ConversationSubmit) {
    if (!conversationModal) return;
    const onError = () => pushToast({ kind: 'error', title: t('comms.createConversation.saveError') });
    if (conversationModal.mode === 'edit') {
      if (payload.kind !== 'channel') return; // Edit is channel-only; the modal never emits a 'dm' payload here.
      editChannel.mutate(
        { channelId: conversationModal.channel.id, body: { name: payload.name, memberIds: payload.memberIds } },
        { onSuccess: () => setConversationModal(null), onError },
      );
      return;
    }
    if (payload.kind === 'channel') {
      createChannel.mutate(
        { name: payload.name, memberIds: payload.memberIds },
        {
          onSuccess: ({ channel: created }) => {
            setConversationModal(null);
            open(created.id);
          },
          onError,
        },
      );
    } else {
      // No API call — just navigate to the (possibly not-yet-created) DM.
      setConversationModal(null);
      openDmWith(payload.memberId);
    }
  }

  const isLoading =
    conversationModal?.mode === 'create'
      ? createChannel.isPending
      : editChannel.isPending;
  const initialValues =
    conversationModal?.mode === 'edit'
      ? { name: conversationModal.channel.kind === 'channel' ? conversationModal.channel.name : '', memberIds: conversationModal.channel.memberIds }
      : undefined;

  return (
    <>
      <div className={styles.panels}>
        <div className={styles.card}>
          <ChatList
            siteId={siteId}
            channels={channels}
            members={members}
            currentUserId={currentUserId}
            selectedId={channel?.id ?? null}
            onSelect={open}
            onCreateClick={() => setConversationModal({ mode: 'create' })}
            lastReadMap={lastReadMap}
            countMap={countMap}
          />
        </div>
        <section className={`${styles.card} ${styles.threadCard}`}>
          {channel ? (
            <ChatThread
              key={channel.id}
              channel={channel}
              siteId={siteId}
              members={members}
              channels={channels}
              unreadSinceAt={unreadSinceAt}
              unreadAtOpenCount={unreadAtOpenCount}
              isVirtual={isVirtual}
              onOpenDm={openDmWith}
              onOpenChannel={open}
              onEdit={() => setConversationModal({ mode: 'edit', channel })}
              onDeleted={() => {
                const fallback = channels.find((c) => c.id !== channel.id);
                if (fallback) open(fallback.id);
              }}
            />
          ) : (
            <div className={styles.threadEmpty}>
              <span className={styles.threadEmptyIcon}>
                <MessageSquareIcon size={48} />
              </span>
              <div className={styles.threadEmptyTitle}>
                {t(channels.length === 0 ? 'comms.thread.noChannelsTitle' : 'comms.thread.noSelectionTitle')}
              </div>
              <div className={styles.threadEmptyHint}>
                {t(channels.length === 0 ? 'comms.thread.noChannelsHint' : 'comms.thread.noSelectionHint')}
              </div>
            </div>
          )}
        </section>
      </div>

      <CreateConversationModal
        key={conversationModal ? `${conversationModal.mode}-${conversationModal.mode === 'edit' ? conversationModal.channel.id : 'new'}` : 'closed'}
        open={conversationModal !== null}
        members={members}
        currentUserId={currentUserId}
        loading={isLoading}
        onCancel={() => setConversationModal(null)}
        onSubmit={handleCreateConversationSubmit}
        initialValues={initialValues}
        canCreateChannel={canManageChannels}
      />

      <Toasts toasts={toasts} onDismiss={dismissToast} />
    </>
  );
}
